import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader } from 'vio-components';
import { ROUTES } from 'vio-constants';
import { usePrevious, useReduxState } from 'vio-libs';

import { AddUpdateForm } from '../components';
import { IMapFeatureRow, IMapFeaturesState, IVaporIO } from '../interfaces/IMapFeatures';
import { convertToFiberName, getFiberColor, isBackboneFiber } from '../lib';
import {
	addPreviewFile,
	requestCreateLayer,
	requestUpdateLayer,
	requestUpdateSource
} from '../redux';
import { useAppDispatch } from '../redux/hooks';

declare const vaporio: IVaporIO;

const AddUpdate: React.FC = () => {
	// HOOKS
	const location = useLocation();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	// LOCAl STATE
	const [ fiber, setFiber ] = useState<IMapFeatureRow>(location.state as IMapFeatureRow);
	// REDUX STATE
	const {
		previewFile,
		requestingCreateLayer,
		requestingCreateSource,
		requestingUpdateLayer,
		requestingUpdateSource,
		requestingUpdateTileserver
	} = useReduxState<IMapFeaturesState>('mapFeatures');
	// REDUX ACTIONS
	const createLayerDispatch = bindActionCreators(requestCreateLayer, dispatch);
	const patchSourceDispatch = bindActionCreators(requestUpdateSource, dispatch);
	const patchLayerDispatch = bindActionCreators(requestUpdateLayer, dispatch);
	const addPreviewFileDispatch = bindActionCreators(addPreviewFile, dispatch);
	// VARIABLES
	const isEditMode = location.pathname.includes('/edit');
	const isUploadMode = location.pathname.includes('/upload');
	const isAddPreviewMode = location.search.includes('preview=true');
	const isAddMode = !isEditMode && !isUploadMode;
	const isLoading = requestingCreateLayer || requestingCreateSource || requestingUpdateTileserver
		|| requestingUpdateSource || requestingUpdateLayer;
	const previousRequestingUpdateSource = usePrevious(requestingUpdateSource);
	const previousRequestingUpdateLayer = usePrevious(requestingUpdateLayer);
	const previousRequestingCreateSource = usePrevious(requestingCreateSource);

	const handleCancel = () => {
		if (location.search.indexOf('preview=true') > -1) {
			addPreviewFileDispatch(null);
		}

		navigate('/assets');
	};

	const handleSubmit = (submitFiber: IMapFeatureRow) => {
		// add_backbone to the name if backbone is true and it doesn't already exist in the name
		if (submitFiber.backbone) {
			if (!isBackboneFiber(submitFiber.name)) {
				submitFiber.name += ' Backbone';
			}
		}

		setFiber({ ...submitFiber });

		const { name, file, future, source, type } = submitFiber;
		const formData = new FormData();

		formData.append('tile_name', type);
		formData.append('file', file);

		if (isAddMode) {
			formData.append('layer_name', convertToFiberName(name));
			createLayerDispatch(formData, {
				data: {
					layer_type: 'line',
					layout: {},
					options: {
						type: 'vector',
						url: `${vaporio.tileserver}/data/fiber.json`
					},
					paint: { 'line-color': getFiberColor(future) },
					source_layer: convertToFiberName(name)
				},
				future,
				name
			});

			return;
		}

		if (isEditMode) {
			patchSourceDispatch(submitFiber.id, {
				...source,
				data: {
					...source.data,
					paint: {
						'line-color': getFiberColor(future)
					}
				},
				future,
				name
			});
		}
		else {
			// ensure new file name matches the existing sources layer_name to maintain the association
			formData.append('layer_name', source.data.source_layer);
			patchLayerDispatch(submitFiber.layer.id, formData);
		}
	};

	// handle when users end up on the page and no preview file exists
	useEffect(() => {
		if (isAddPreviewMode && !previewFile?.file) {
			navigate(ROUTES.MAP);
		}
	}, [ isAddPreviewMode, previewFile ]);

	useEffect(() => {
		if (isEditMode && !isLoading) {
			if (previousRequestingUpdateSource && !requestingUpdateSource) {
				navigate('/assets');
			}
		}
	}, [ requestingUpdateSource ]);

	useEffect(() => {
		if (isUploadMode && !isLoading) {
			if (previousRequestingUpdateLayer && !requestingUpdateLayer) {
				navigate('/assets');
			}
		}
	}, [ requestingUpdateLayer ]);

	useEffect(() => {
		if ((isAddMode || isAddPreviewMode) && !isLoading) {
			if (previousRequestingCreateSource && !requestingCreateSource) {
				// race condition exists here with the above useEffect about navigate(ROUTES.MAP)
				// using a timeout to ensure the user is navigated back to the assets list and not the map
 				setTimeout(() => addPreviewFileDispatch(null), 500);
 				navigate('/assets');
			}
		}
	}, [ requestingCreateSource ]);

	const renderTitle = (): string => {
		let title = 'Edit Asset';

		if (isAddMode && !isAddPreviewMode) {
			title = 'Create Asset';
		}
		else if (isAddPreviewMode) {
			title = 'Create Asset from Preview';
		}
		else if (isUploadMode) {
			title = 'Upload Fiber';
		}

		return title;
	};

	return (
		<div className='container p-4'>
			<div className='row'>
				{ isLoading && <Loader message={ t('PROCESSING') } /> }

				{ !isLoading && <div className='col'>
					<h1>{ renderTitle() }</h1>

					<AddUpdateForm
						isEditMode={ isEditMode }
						isUploadMode={ isUploadMode }
						isAddPreviewMode={ isAddPreviewMode }
						fiber={ fiber }
						previewFile={ previewFile }
						handleCancel={ handleCancel }
						handleSubmit={ handleSubmit } />
				</div> }
			</div>
		</div>
	);
};

export default AddUpdate;
